import React from 'react';
import { Line } from 'react-chartjs-2';
import { formatConv, lineChartOptions } from './analyticsFormat';
import { registerAdminCharts } from './registerAdminCharts';

registerAdminCharts();

export default function GuestFunnelSection({
  gf,
  palette,
  guestFunnelDailyData,
  funnelTrendShowSubmits,
  setFunnelTrendShowSubmits,
  funnelTrendShowMerges,
  setFunnelTrendShowMerges,
}) {
  if (!gf) return null;

  return (
    <div className="adminGuestFunnel">
      <div className="adminGuestFunnel-header">
        <div>
          <h2 className="adminGuestFunnel-title">Public card · Guest Save Funnel</h2>
          <p className="adminGuestFunnel-sub adminMutedText">
            Product analytics (not GA). The date window on this page drives the funnel and daily user growth.
            Custom ranges: pick start and end dates, then Apply.
            {gf.range?.start && gf.range?.endExclusive ? (
              <>
                {' '}
                <strong>
                  Window: {gf.range.start.slice(0, 10)} →{' '}
                  {new Date(new Date(gf.range.endExclusive).getTime() - 1).toISOString().slice(0, 10)} (UTC,
                  end exclusive)
                </strong>
              </>
            ) : null}
          </p>
          {gf.note ? <p className="adminGuestFunnel-note adminMutedText">{gf.note}</p> : null}
        </div>
      </div>

      <div className="adminGuestFunnel-insights">
        <div
          className={`adminGuestFunnel-health adminGuestFunnel-health--${
            gf.funnelHealth?.level === 'critical'
              ? 'critical'
              : gf.funnelHealth?.level === 'warning'
                ? 'warning'
                : 'good'
          }`}
        >
          <div className="adminGuestFunnel-health-title">Funnel health</div>
          <ul className="adminGuestFunnel-health-list">
            {(gf.funnelHealth?.bullets || ['No health summary yet.']).map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        </div>
        <div className="adminGuestFunnel-guestUser">
          <div className="adminGuestFunnel-guestUser-label">{gf.guestToUserConversion?.label || 'Guest → user conversion'}</div>
          <div className="adminGuestFunnel-guestUser-rate">{formatConv(gf.guestToUserConversion?.rate)}</div>
          <div className="adminGuestFunnel-guestUser-sub adminMutedText">
            {gf.guestToUserConversion?.sublabel || 'Merged contacts / guest saves'}
          </div>
          <div className="adminGuestFunnel-guestUser-abs adminMutedText">
            {gf.guestToUserConversion?.numerator ?? 0} merged contacts / {gf.guestToUserConversion?.denominator ?? 0}{' '}
            guest saves
          </div>
          {gf.guestToUserConversion?.mergeSessionRate != null ? (
            <div className="adminGuestFunnel-guestUser-meta adminMutedText">
              Merge attempts / saves: {formatConv(gf.guestToUserConversion.mergeSessionRate)} (
              {gf.guestToUserConversion.mergeAttempts ?? 0} / {gf.guestToUserConversion.denominator ?? 0})
            </div>
          ) : null}
        </div>
      </div>

      {gf.weakestStep ? (
        <p className="adminGuestFunnel-dropoff adminMutedText">
          <span className="adminGuestFunnel-dropoff-label">Biggest drop-off</span>
          <span className="adminGuestFunnel-dropoff-text">
            {' '}
            {gf.weakestStep.fromLabel} → {gf.weakestStep.toLabel}{' '}
            <span className="adminGuestFunnel-dropoff-pct">({gf.weakestStep.dropoffPct}% not converting)</span>
          </span>
        </p>
      ) : null}

      <div className="adminGuestFunnel-coreKpis">
        {(gf.coreConversionKpis || []).map((k) => (
          <div key={k.id} className="adminGuestFunnel-coreKpi">
            <span className="adminGuestFunnel-coreKpi-label">{k.label}</span>
            <span className="adminGuestFunnel-coreKpi-rate">{formatConv(k.rate)}</span>
            <span className="adminGuestFunnel-coreKpi-abs adminMutedText">
              {k.numerator ?? 0} / {k.denominator ?? 0}
            </span>
            <span className="adminGuestFunnel-coreKpi-sublabel adminMutedText">{k.sublabel}</span>
          </div>
        ))}
      </div>

      <div className="adminGuestFunnel-kpis">
        <div className="adminGuestFunnel-kpi">
          <span className="adminGuestFunnel-kpi-value">
            {gf.stages?.find((s) => s.key === 'nfc_public_card_viewed')?.count ?? 0}
          </span>
          <span className="adminGuestFunnel-kpi-label">NFC card views</span>
        </div>
        <div className="adminGuestFunnel-kpi">
          <span className="adminGuestFunnel-kpi-value">
            {gf.stages?.find((s) => s.key === 'digital_public_card_viewed')?.count ?? 0}
          </span>
          <span className="adminGuestFunnel-kpi-label">Digital card views</span>
        </div>
        <div className="adminGuestFunnel-kpi">
          <span className="adminGuestFunnel-kpi-value">
            {gf.stages?.find((s) => s.key === 'guest_save_modal_open')?.count ?? 0}
          </span>
          <span className="adminGuestFunnel-kpi-label">Save to Velox opens</span>
        </div>
        <div className="adminGuestFunnel-kpi">
          <span className="adminGuestFunnel-kpi-value">
            {gf.stages?.find((s) => s.key === 'guest_email_submitted')?.count ?? 0}
          </span>
          <span className="adminGuestFunnel-kpi-label">Email submits</span>
        </div>
        <div className="adminGuestFunnel-kpi adminGuestFunnel-kpi-accent">
          <span className="adminGuestFunnel-kpi-value">
            {gf.stages?.find((s) => s.key === 'guest_immediate_save_success')?.count ?? 0}
          </span>
          <span className="adminGuestFunnel-kpi-label">Guest saves created</span>
        </div>
        <div className="adminGuestFunnel-kpi">
          <span className="adminGuestFunnel-kpi-value">
            {gf.stages?.find((s) => s.key === 'guest_merge_imported')?.count ?? 0}
          </span>
          <span className="adminGuestFunnel-kpi-label">Contacts merged in</span>
        </div>
      </div>

      <div className="adminGuestFunnel-body">
        <div className="adminGuestFunnel-col">
          <h3 className="adminGuestFunnel-col-title">Funnel steps</h3>
          <ol className="adminGuestFunnel-steps">
            {(gf.stages || []).map((s) => (
              <li key={s.key} className="adminGuestFunnel-step">
                <span className="adminGuestFunnel-step-label">{s.label}</span>
                <span className="adminGuestFunnel-step-count">
                  {s.count}
                  {s.eventCount != null && s.eventCount > 0 ? (
                    <span className="adminGuestFunnel-step-meta"> · {s.eventCount} merge ops</span>
                  ) : null}
                </span>
              </li>
            ))}
          </ol>
        </div>
        <div className="adminGuestFunnel-col">
          <h3 className="adminGuestFunnel-col-title">Conversion rates</h3>
          <ul className="adminGuestFunnel-conv">
            {(gf.conversions || []).map((c) => (
              <li key={c.id} className="adminGuestFunnel-conv-row">
                <span className="adminGuestFunnel-conv-label">{c.label}</span>
                <span className="adminGuestFunnel-conv-value">{formatConv(c.rate)}</span>
                <span className="adminGuestFunnel-conv-detail adminMutedText">
                  {c.numerator ?? 0} / {c.denominator ?? 0}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {gf.dailySaves?.length ? (
        <div className="adminGuestFunnel-chart chart">
          <div className="adminGuestFunnel-chartHead">
            <h3 className="adminGuestFunnel-col-title adminGuestFunnel-chartTitle">Daily funnel trend</h3>
            <div className="adminGuestFunnel-chartToggles">
              <label className="adminGuestFunnel-chartToggle">
                <input
                  type="checkbox"
                  checked={funnelTrendShowSubmits}
                  onChange={(e) => setFunnelTrendShowSubmits(e.target.checked)}
                />
                Email submits
              </label>
              <label className="adminGuestFunnel-chartToggle">
                <input
                  type="checkbox"
                  checked={funnelTrendShowMerges}
                  onChange={(e) => setFunnelTrendShowMerges(e.target.checked)}
                />
                Merge attempts
              </label>
            </div>
          </div>
          <Line
            data={guestFunnelDailyData}
            options={lineChartOptions(palette, { maxRotation: 45, precision: 0 })}
          />
        </div>
      ) : null}
    </div>
  );
}
